import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuth } from '@/contexts/AuthContext'
import { authAPI, subjectsAPI, setUserProfile, streamsAPI, collegesAPI } from '@/services/api'
import { Badge } from '@/components/ui/badge.jsx'
import { BookOpen, User, Loader2, Save, ArrowLeft, Trash2 } from 'lucide-react'

const ProfilePage = () => {
  const navigate = useNavigate()
  const { user, isLoggedIn, refreshProfile, updateUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [subjects, setSubjects] = useState([])
  const [selectedSubjectDetails, setSelectedSubjectDetails] = useState([])
  const [confirmDeleteSubjectId, setConfirmDeleteSubjectId] = useState(null)
  const [availableStreams, setAvailableStreams] = useState([])
  const [streamsLoading, setStreamsLoading] = useState(false)
  const [availableColleges, setAvailableColleges] = useState([])
  const [collegesLoading, setCollegesLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    university: '',
    year: '',
    stream: '',
    selectedSubjects: []
  })

  // Normalize IDs to strings to avoid type mismatches (e.g., UUID vs number)
  const normalizeSubjectId = (id) => {
    if (id === null || id === undefined) return ''
    return typeof id === 'string' ? id.trim() : String(id)
  }
  const normalizeIds = (ids) => {
    return Array.isArray(ids) ? ids.map(normalizeSubjectId).filter(Boolean) : []
  }

  useEffect(() => {
    // Redirect if not logged in
    if (!isLoggedIn) {
      navigate('/login')
      return
    }

    // Load user data (prefer latest from backend if available)
    const initProfile = async () => {
      try {
        let profile = user
        if (!profile) {
          profile = await refreshProfile()
        }
        if (profile) {
          setFormData({
            name: profile.name || '',
            email: profile.email || '',
            university: profile.university || '',
            year: profile.year || '',
            stream: profile.stream || '',
            selectedSubjects: (profile.selectedSubjects || []).map(s => {
              if (typeof s === 'string') return s
              return s._id || s.id
            })
          })
        }
      } catch (e) {
        console.error('Failed to initialize profile:', e)
      }
    }
    initProfile()
  }, [user, isLoggedIn, navigate])
  
  // Fetch streams list for stream selection
  useEffect(() => {
    const fetchStreams = async () => {
      try {
        setStreamsLoading(true)
        const selectedUniversity = (formData.university || '').trim()
        const hasValidUniversity = selectedUniversity && availableColleges.includes(selectedUniversity)
        if (!hasValidUniversity) {
          setAvailableStreams([])
          return
        }
        const res = await streamsAPI.getAll({ college: selectedUniversity })
        const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
        const names = arr.map((s) => s?.name || s?.title || (typeof s === 'string' ? s : '')).filter(Boolean)
        setAvailableStreams(names)
      } catch (e) {
        console.error('Failed to fetch streams:', e)
        setAvailableStreams([])
      } finally {
        setStreamsLoading(false)
      }
    }
    fetchStreams()
  }, [formData.university, availableColleges])
  
  // Fetch colleges for university dropdown
  useEffect(() => {
    const fetchColleges = async () => {
      try {
        setCollegesLoading(true)
        const res = await collegesAPI.getAll()
        const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
        const names = arr.map((c) => c?.name || (typeof c === 'string' ? c : '')).filter(Boolean)
        setAvailableColleges(names)
      } catch (e) {
        console.error('Failed to fetch colleges:', e)
        setAvailableColleges([])
      } finally {
        setCollegesLoading(false)
      }
    }
    fetchColleges()
  }, [])
  
  // Fetch and filter subjects by selected stream, year, and VALID university
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const selectedUniversity = (formData.university || '').trim()
        const selectedYear = (formData.year || '').trim()
        const hasValidUniversity = selectedUniversity && availableColleges.includes(selectedUniversity)
        if (!hasValidUniversity || !selectedYear) {
          setSubjects([])
          return
        }
        const filters = { year: selectedYear, college: selectedUniversity }
        const response = await subjectsAPI.getAll(filters)
        const raw = response?.data ?? (Array.isArray(response) ? response : [])
        const selectedStream = (formData.stream || '').trim()
        const fallbackStream = (user?.stream || '').trim()
        const streamToUse = selectedStream || fallbackStream
        const filtered = (Array.isArray(raw) ? raw : []).filter((s) => {
          const sStream = String(s?.stream || '').trim()
          const matchStream = streamToUse ? sStream === streamToUse : true
          return matchStream
        })
        setSubjects(filtered)
      } catch (err) {
        console.error('Error fetching subjects:', err)
      }
    }
    fetchSubjects()
  }, [formData.year, formData.stream, formData.university, availableColleges])

  // Fetch details for currently selected subjects so they always render
  useEffect(() => {
    const loadSelectedDetails = async () => {
      try {
        const ids = Array.isArray(formData.selectedSubjects) ? formData.selectedSubjects.filter(Boolean) : []
        if (ids.length === 0) {
          setSelectedSubjectDetails([])
          return
        }
        const uniqueIds = Array.from(new Set(ids))
        const results = await Promise.all(uniqueIds.map(async (id) => {
          try {
            const resp = await subjectsAPI.getById(id)
            return resp?.data || resp
          } catch (e) {
            return null
          }
        }))
        setSelectedSubjectDetails(results.filter(Boolean))
      } catch (e) {
        console.error('Failed to load selected subject details:', e)
      }
    }
    loadSelectedDetails()
  }, [formData.selectedSubjects])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    })
  }

  const toggleSubjectSelection = (subjectId) => {
    setFormData(prev => {
      const id = normalizeSubjectId(subjectId)
      const current = normalizeIds(prev.selectedSubjects)
      const exists = current.includes(id)
      return {
        ...prev,
        selectedSubjects: exists
          ? current.filter(x => x !== id)
          : [...current, id]
      }
    })
  }

  const handleConfirmDelete = () => {
    const id = confirmDeleteSubjectId
    setConfirmDeleteSubjectId(null)
    if (!id) return
    setFormData(prev => {
      const current = normalizeIds(prev.selectedSubjects)
      return {
        ...prev,
        selectedSubjects: current.filter(x => x !== normalizeSubjectId(id))
      }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      const payload = { 
        name: formData.name, 
        university: formData.university, 
        year: formData.year, 
        stream: formData.stream, 
        selectedSubjects: normalizeIds(formData.selectedSubjects) 
      }
      const response = await authAPI.updateProfile(payload)
      if (response && response.success && response.user) {
        setSuccess('Profile updated successfully!')
        // Build next user with selected subject IDs from response.selectedSubjects
        const selectedIds = Array.isArray(response.selectedSubjects)
          ? response.selectedSubjects.map(s => (typeof s === 'string' ? normalizeSubjectId(s) : normalizeSubjectId(s.id))).filter(Boolean)
          : []
        const nextUser = { ...response.user, selectedSubjects: selectedIds }
        // Update context and localStorage cohesively
        updateUser(nextUser)
        setFormData(prev => ({
          ...prev,
          name: response.user.name || prev.name,
          university: response.user.university || prev.university,
          year: response.user.year || prev.year,
          stream: response.user.stream || prev.stream,
          selectedSubjects: selectedIds
        }))
      } else {
        throw new Error(response?.message || 'Update failed')
      }
    } catch (err) {
      setError('Failed to update profile. Please try again.')
      console.error('Profile update error:', err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <Button 
          variant="ghost" 
          className="mb-6" 
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="max-w-2xl mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto bg-primary/10 p-4 rounded-full w-20 h-20 flex items-center justify-center mb-4">
              <User className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-2xl">Your Profile</CardTitle>
            <CardDescription>
              Update your personal information and preferences
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="p-3 text-sm bg-green-50 border border-green-200 rounded-md flex items-center justify-between">
                  <span className="text-green-700">{success}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:text-primary/80"
                    onClick={() => navigate('/dashboard')}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Dashboard
                  </Button>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={saving}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={true} // Email cannot be changed
                  required
                />
                <p className="text-xs text-gray-500">Email address cannot be changed</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="university">University</Label>
                <Select 
                  value={formData.university}
                  onValueChange={(value) => handleSelectChange('university', value)}
                  disabled={saving || collegesLoading || availableColleges.length === 0}
                >
                  <SelectTrigger disabled={saving || collegesLoading || availableColleges.length === 0}>
                    <SelectValue placeholder={collegesLoading ? 'Loading universities...' : (availableColleges.length === 0 ? 'No universities available yet' : 'Select your university')} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableColleges.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {availableColleges.length === 0 && !collegesLoading && (
                  <p className="text-xs text-muted-foreground">Universities appear when admins add colleges.</p>
                )}
              </div>
              
              {/* Stream selection */}
              <div className="space-y-2">
                <Label htmlFor="stream">Stream</Label>
                <Select 
                  value={formData.stream}
                  onValueChange={(value) => handleSelectChange('stream', value)}
                  disabled={saving || streamsLoading || availableStreams.length === 0 || !formData.university}
                >
                  <SelectTrigger disabled={saving || streamsLoading || availableStreams.length === 0 || !formData.university}>
                    <SelectValue placeholder={streamsLoading ? 'Loading streams...' : (!formData.university ? 'Select university first' : (availableStreams.length === 0 ? 'No streams available yet' : 'Select your stream'))} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStreams.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {availableStreams.length === 0 && !streamsLoading && (
                  <p className="text-xs text-muted-foreground">Streams appear when admins upload content tied to a stream.</p>
                )}
                {formData.stream ? (
                  <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                    <span>Selected stream:</span>
                    <Badge variant="secondary">{formData.stream}</Badge>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">Please select a stream.</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="year">Year</Label>
                <Select
                  value={formData.year}
                  onValueChange={(value) => setFormData({ ...formData, year: value })}
                  disabled={saving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1st">1st Year</SelectItem>
                    <SelectItem value="2nd">2nd Year</SelectItem>
                    <SelectItem value="3rd">3rd Year</SelectItem>
                    <SelectItem value="4th">4th Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-3">
                {(!formData.university || !availableColleges.includes(formData.university)) && (
                  <p className="text-xs text-gray-500">Select your university to view available subjects.</p>
                )}
                {!formData.year && (
                  <p className="text-xs text-gray-500">Select your year to view available subjects.</p>
                )}
                {formData.year && formData.university && availableColleges.includes(formData.university) && subjects.length === 0 && (
                  <p className="text-sm text-gray-500">No subjects available for the selected year and university.</p>
                )}

                {subjects.length > 0 && (
                  <>
                    {/* Selected & Available Subjects */}
                    {(() => {
                      const selectedIds = Array.isArray(formData.selectedSubjects) ? formData.selectedSubjects : []
                      const selectedIdsNorm = selectedIds.map(normalizeSubjectId)
                      const withId = (s) => s?._id || s?.id
                      // Merge subjects and selected details to ensure names show
                      const subjectMap = new Map()
                      ;(subjects || []).forEach(s => subjectMap.set(withId(s), s))
                      ;(selectedSubjectDetails || []).forEach(s => subjectMap.set(withId(s), s))
                      const combinedSubjects = Array.from(subjectMap.values()).filter(Boolean)
                      const selectedList = combinedSubjects.filter(s => selectedIdsNorm.includes(normalizeSubjectId(withId(s))))
                      const availableList = (subjects || []).filter(s => !selectedIdsNorm.includes(normalizeSubjectId(withId(s))))
                      return (
                        <>
                          <div className="space-y-2">
                            <p className="text-sm font-medium">Selected Subjects</p>
                            {selectedList.length === 0 ? (
                              <p className="text-xs text-gray-500">No subjects selected yet.</p>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {selectedList.map(subject => {
                                  const id = subject._id || subject.id
                                  return (
                                    <Badge key={id} variant="secondary" className="text-sm flex items-center gap-2">
                                      <span>
                                        {subject.name || id}
                                        {subject.subjectType ? (
                                          <span className="ml-2 text-xs text-gray-500">({subject.subjectType})</span>
                                        ) : null}
                                      </span>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => setConfirmDeleteSubjectId(id)}
                                      >
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        Remove
                                      </Button>
                                    </Badge>
                                  )
                                })}
                              </div>
                            )}
                          </div>

                          {/* Subjects with dynamic selection state */}
                          <div className="space-y-2">
                            <Label>Choose Your Subjects</Label>
                            {subjects.length === 0 ? (
                              <p className="text-xs text-gray-500">No subjects available for the selected year and stream.</p>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {subjects.map(subject => {
                                  const id = subject._id || subject.id
                                  const isSelected = selectedIdsNorm.includes(normalizeSubjectId(id))
                                  return (
                                    <label key={id} className="flex items-center space-x-2 p-2 border rounded-md">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleSubjectSelection(id)}
                                      />
                                      <span className="text-sm">
                                        {subject.name}
                                        {subject.subjectType ? (
                                          <span className="ml-2 text-xs text-gray-500">({subject.subjectType})</span>
                                        ) : null}
                                      </span>
                                    </label>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        </>
                      )
                    })()}
                  </>
                )}
              </div>

              <Button type="submit" className="w-full mt-6" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
              <div className="mt-3 flex justify-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:text-primary/80"
                  onClick={() => navigate('/dashboard')}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Dashboard
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        {/* Confirmation Popup for Deleting Selected Subject */}
        {confirmDeleteSubjectId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-sm w-full">
              <CardHeader>
                <CardTitle>Remove subject?</CardTitle>
                <CardDescription>
                  Are you sure you want to remove this subject from your selections?
                  This will not delete any notes or videos.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmDeleteSubjectId(null)}>Cancel</Button>
                <Button variant="destructive" onClick={handleConfirmDelete}>Remove</Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProfilePage